import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, FileText, Users, Settings, CheckCheck, Inbox } from 'lucide-react';
import { notifications as notifApi } from '../api';
import PushNotificationOptIn from './PushNotificationOptIn';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'team', label: 'Team' },
  { key: 'system', label: 'System' },
];

function getCategory(notification) {
  // F184/F196 — decision rows (go_no_go, allocation_change, etc.) are
  // always attached to a proposal so they belong in the Proposals tab.
  if ((notification.type || '').toLowerCase() === 'decision') return 'proposals';
  const t = (notification.title || '').toLowerCase();
  const b = (notification.body || '').toLowerCase();
  if (t.includes('proposal') || t.includes('bid') || t.includes('tender') || b.includes('proposal')) return 'proposals';
  if (t.includes('team') || t.includes('person') || t.includes('member') || t.includes('assigned') || b.includes('team')) return 'team';
  return 'system';
}

function getCategoryIcon(category) {
  switch (category) {
    case 'proposals': return FileText;
    case 'team': return Users;
    default: return Settings;
  }
}

function getNavigationPath(notification) {
  // Prefer structured fields painted by the backend producers (approvals,
  // comments, deadlines all set reference_type + reference_id). Falls back
  // to type-only routing for notifications without a reference (e.g.
  // ai_usage_warning), then to a title heuristic for legacy rows.
  const refType = notification.reference_type;
  const refId = notification.reference_id;
  if (refType && refId) {
    if (refType === 'proposal') return `/proposals/${refId}/build`;
    if (refType === 'project') return `/projects/${refId}`;
    if (refType === 'person') return `/people/${refId}`;
    if (refType === 'tender') return `/tenders`;
  }
  const type = (notification.type || '').toLowerCase();
  if (type === 'ai_usage_warning') return '/ai-usage';
  if (type === 'deadline_reminder' && refId) return `/proposals/${refId}/build`;
  // F184/F196 — decision notifications deep-link to the Decision Log
  // page so the user sees the full audit context instead of just the
  // proposal. The Decision Log page itself links back to the proposal.
  if (type === 'decision') return '/decisions';

  const t = (notification.title || '').toLowerCase();
  if (t.includes('proposal') || t.includes('approval') || t.includes('comment')) return '/proposals';
  if (t.includes('team') || t.includes('person') || t.includes('mention')) return '/people';
  if (t.includes('tender')) return '/tenders';
  if (t.includes('project')) return '/projects';
  return null;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifList, setNotifList] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [animateBadge, setAnimateBadge] = useState(false);
  const prevCountRef = useRef(0);
  const panelRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);

  const loadCount = useCallback(async () => {
    try {
      const res = await notifApi.unreadCount();
      const newCount = res.data.unread_count;
      consecutiveFailuresRef.current = 0; // reset on success
      if (newCount > prevCountRef.current) {
        setAnimateBadge(true);
        setTimeout(() => setAnimateBadge(false), 600);
      }
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch {
      consecutiveFailuresRef.current += 1;
      // Only log on first failure to avoid console spam
      if (consecutiveFailuresRef.current === 1) {
        console.warn('Notification polling: backend unreachable, will retry silently');
      }
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await notifApi.list({ limit: 50 });
      const items = Array.isArray(res.data) ? res.data : res.data.items || [];
      setNotifList(items);
    } catch { /* silent — user can retry by reopening panel */ }
  }, []);

  useEffect(() => {
    loadCount();
    // Use adaptive polling: back off when failing, recover when succeeding
    const interval = setInterval(() => {
      const failures = consecutiveFailuresRef.current;
      // Skip poll if backend has been down for a while (exponential backoff)
      if (failures > 2 && Math.random() > 1 / Math.min(failures, 10)) return;
      loadCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadCount]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleToggle = () => {
    if (!open) {
      loadNotifications();
    }
    setOpen(!open);
  };

  const handleMarkRead = async (id) => {
    try {
      await notifApi.markRead(id);
      setNotifList(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { console.error('Failed to mark notification as read:', err); }
  };

  const handleMarkAllRead = async () => {
    try {
      await notifApi.markAllRead();
      setUnreadCount(0);
      setNotifList(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) { console.error('Failed to mark all notifications as read:', err); }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkRead(notification.id);
    }
    // Always close the panel so the click feels responsive, even if there's
    // no navigation target. Otherwise the panel just sits open and the only
    // visible change is the unread dot disappearing — users read that as
    // "nothing happened".
    setOpen(false);
    const path = getNavigationPath(notification);
    if (path) navigate(path);
  };

  const filtered = activeTab === 'all'
    ? notifList
    : notifList.filter(n => getCategory(n) === activeTab);

  return (
    <>
      <button
        className="btn btn-ghost btn-sm notification-bell-btn"
        onClick={handleToggle}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell size={18} aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            className={`notification-badge${animateBadge ? ' notification-badge-animate' : ''}`}
            aria-hidden="true"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Overlay */}
      {open && <div className="notification-overlay" onClick={() => setOpen(false)} />}

      {/* Slide-out panel */}
      <div
        ref={panelRef}
        className={`notification-panel${open ? ' notification-panel-open' : ''}`}
        role="dialog"
        aria-label="Notifications"
      >
        <div className="notification-panel-header">
          <h2>Notifications</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {unreadCount > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={handleMarkAllRead} style={{ fontSize: 12 }}>
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} aria-label="Close notifications">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* F302 V2 — opt-in CTA for browser push. Self-hides when push
            already enabled, blocked, or browser doesn't support PushManager. */}
        <PushNotificationOptIn />

        <div className="notification-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`notification-tab${activeTab === tab.key ? ' notification-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="notification-list">
          {filtered.length === 0 ? (
            <div className="notification-empty">
              <Inbox size={40} strokeWidth={1.5} />
              <p className="notification-empty-title">All caught up!</p>
              <p className="notification-empty-desc">No new notifications.</p>
            </div>
          ) : (
            filtered.map(n => {
              const category = getCategory(n);
              const CategoryIcon = getCategoryIcon(category);
              return (
                <div
                  key={n.id}
                  className={`notification-item${!n.is_read ? ' notification-item-unread' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNotificationClick(n);
                    }
                  }}
                >
                  <div className={`notification-item-icon notification-item-icon-${category}`}>
                    <CategoryIcon size={16} />
                  </div>
                  <div className="notification-item-content">
                    <div className="notification-item-title">
                      {n.title}
                      {!n.is_read && <span className="notification-unread-dot" />}
                    </div>
                    {n.body && (
                      <div className="notification-item-body">{n.body.substring(0, 120)}</div>
                    )}
                    <div className="notification-item-time">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
